import { memo, useCallback, useMemo } from 'react';

import {
  alpha,
  Box,
  Chip,
  makeStyles,
  Slide,
  useTheme,
} from '@material-ui/core';
import { Close } from '@material-ui/icons';

import { TagInterface } from 'store/reducers/tags';

const TAG_MAX_LENGTH = 14;
const TAG_ICON_SIZE = 14;

const useStyles = makeStyles(({ typography }) => ({
  chipLabel: {
    fontSize: typography.pxToRem(11),
    fontWeight: typography.fontWeightMedium as number,
    cursor: 'pointer',
  },
}));

interface TagItemProps {
  tag: TagInterface;
  tagAdd?: string;
  onRemove?: (tagId?: string) => void;
}

const TagItem = ({ tag, tagAdd, onRemove }: TagItemProps) => {
  const classes = useStyles();
  const theme = useTheme();

  const { id, nome, cor } = tag;

  const colors = useMemo(() => {
    const isDark = theme.palette.type === 'dark';
    const baseColor =
      cor || (isDark ? theme.palette.grey[300] : theme.palette.text.primary);

    return {
      backgroundColor: cor,
      borderColor: alpha(baseColor, 0.3),
      textColor: theme.palette.getContrastText(baseColor),
    };
  }, [cor, theme]);

  const tagLabel =
    nome && nome.length > TAG_MAX_LENGTH
      ? `${nome.slice(0, TAG_MAX_LENGTH)}...`
      : nome;

  const handleDelete = useCallback(() => {
    if (onRemove) onRemove(id);
  }, [onRemove, id]);

  const chipElement = (
    <Box title={nome || ''}>
      <Chip
        deleteIcon={
          <Close
            titleAccess="Remover tag"
            style={{
              width: TAG_ICON_SIZE,
              height: TAG_ICON_SIZE,
              color: colors.textColor,
            }}
          />
        }
        size="small"
        classes={{
          labelSmall: classes.chipLabel,
        }}
        label={tagLabel}
        style={{
          backgroundColor: colors.backgroundColor,
          border: `1px solid ${colors.borderColor}`,
          color: colors.textColor,
        }}
        onDelete={onRemove ? handleDelete : undefined}
        role="button"
        aria-label={
          onRemove ? `Tag ${nome}, clique para remover` : `Tag ${nome}`
        }
      />
    </Box>
  );

  return id === tagAdd ? (
    <Slide in direction="left">
      {chipElement}
    </Slide>
  ) : (
    chipElement
  );
};

const MemoizedTagItem = memo(TagItem, (prevProps, nextProps) => {
  return (
    prevProps.tag.id === nextProps.tag.id &&
    prevProps.tag.cor === nextProps.tag.cor &&
    prevProps.onRemove === nextProps.onRemove
  );
});

export { MemoizedTagItem as TagItem };
